import Notifications from 'react-notification-system-redux';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import {
  createOrUpdateAudioAbbreviation as createOrUpdateAudioAbbreviationService,
  createOrUpdateMultipleAudioAbbreviations as createOrUpdateMultipleAudioAbbreviationsService,
  deleteAudioAbbreviation as deleteAudioAbbreviationService,
  getAllAudioAbbreviations as getAllAudioAbbreviationsService,
} from '../../utilities/ServiceManager';
import { setAllAudioAbbreviations as setAudioAbbreviations } from './actions';
import { createOrUpdateMultipleAudioAbbreviationsFail } from './actionsSagas';
import * as typeSagas from './actionTypesSagas';

// Helper functions
function isBadRequest(exception) {
  return (
    exception?.code === 'ERR_BAD_REQUEST' ||
    exception?.request?.status === 400 ||
    exception?.response?.status === 400
  );
}

function showNotificationErrorFromException(
  exception,
  defaultMessage = 'Something went wrong, please try again.',
) {
  let message = defaultMessage;

  if (
    exception?.response?.data?.error?.code &&
    exception.response.data.error.code !== 'UsageError' &&
    exception.response.data.error.message
  ) {
    message = exception.response.data.error.message;
  }
  return Notifications.show({ title: 'Ops!', message }, 'error');
}

// Audio abbreviation saga functions
function* getAllAudioAbbreviationsWatcherSaga() {
  yield takeLatest(
    typeSagas.GET_ALL_AUDIO_ABBREVIATIONS,
    getAllAudioAbbreviationsWorkerSaga,
  );
}

function* getAllAudioAbbreviationsWorkerSaga(action) {
  try {
    const { search, page, skip, limit } = action.payload;
    const audioAbbreviations = yield call(
      getAllAudioAbbreviationsService,
      search,
      page,
      skip,
      limit,
    );
    yield put(setAudioAbbreviations(audioAbbreviations));
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not get audio abbreviations. Please try again later.',
      ),
    );
  }
}

function* createOrUpdateAudioAbbreviationWatcherSaga() {
  yield takeLatest(
    typeSagas.CREATE_OR_UPDATE_AUDIO_ABBREVIATION,
    createOrUpdateAudioAbbreviationWorkerSaga,
  );
}

function* createOrUpdateAudioAbbreviationWorkerSaga(action) {
  yield handleAudioAbbreviationCreationOrUpdate(action.payload.abbreviation);
}

function* createOrUpdateMultipleAudioAbbreviationsWatcherSaga() {
  yield takeLatest(
    typeSagas.CREATE_OR_UPDATE_MULTIPLE_AUDIO_ABBREVIATIONS,
    createOrUpdateMultipleAudioAbbreviationsWorkerSaga,
  );
}

function* createOrUpdateMultipleAudioAbbreviationsWorkerSaga(action) {
  try {
    yield call(
      createOrUpdateMultipleAudioAbbreviationsService,
      action.payload.abbreviations,
    );
    const updatedAbbreviations = yield call(getAllAudioAbbreviationsService);
    yield put(setAudioAbbreviations(updatedAbbreviations));
    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'Audio abbreviations imported successfully.',
          autoDismiss: 5,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(createOrUpdateMultipleAudioAbbreviationsFail());

    if (isBadRequest(exception)) {
      yield put(
        showNotificationErrorFromException(
          exception,
          exception.response.data || 'Invalid input',
        ),
      );
    } else {
      yield put(
        showNotificationErrorFromException(
          exception,
          'Could not create/update audio abbreviations. Please try again later.',
        ),
      );
    }
  }
}

function* handleAudioAbbreviationCreationOrUpdate(item) {
  try {
    yield call(createOrUpdateAudioAbbreviationService, item);
    yield put(
      Notifications.show(
        {
          title: 'Processing',
          message: `Audio abbreviation is being created in the background and added to the creation queue`,
          autoDismiss: 5,
        },
        'success',
      ),
    );
  } catch (exception) {
    if (isBadRequest(exception)) {
      yield put(
        showNotificationErrorFromException(
          exception,
          exception.response.data || 'Invalid input',
        ),
      );
    } else {
      yield put(
        showNotificationErrorFromException(
          exception,
          `Could not create/update audio abbreviation "${item.text}". Please try again later.`,
        ),
      );
    }
  }
}

function* deleteAudioAbbreviationWatcherSaga() {
  yield takeLatest(
    typeSagas.DELETE_AUDIO_ABBREVIATION,
    deleteAudioAbbreviationWorkerSaga,
  );
}

function* deleteAudioAbbreviationWorkerSaga(action) {
  try {
    yield call(deleteAudioAbbreviationService, action.payload.abbreviation);
    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: `Audio abbreviation "${action.payload.abbreviation.text}" deleted successfully.`,
          autoDismiss: 5,
        },
        'success',
      ),
    );
    // Refresh the list after deletion
    yield call(getAllAudioAbbreviationsWorkerSaga, { payload: {} });
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        `Could not delete audio abbreviation "${action.payload.abbreviation.text}". Please try again later.`,
      ),
    );
  }
}

// Main saga watcher
export default function* audioAbbreviationsSaga() {
  yield all([
    getAllAudioAbbreviationsWatcherSaga(),
    createOrUpdateAudioAbbreviationWatcherSaga(),
    createOrUpdateMultipleAudioAbbreviationsWatcherSaga(),
    deleteAudioAbbreviationWatcherSaga(),
  ]);
}
